import React from "react";

import { compact, isEmpty, map } from "lodash/fp";
import { shallow } from "zustand/shallow";

import Typography from "@mui/material/Typography";

import Contributors from "@app/analytics/Pulse/components/Contributors";
import SessionListPopover from "@app/analytics/Pulse/components/SessionListPopover";
import { Content } from "@app/analytics/Pulse/components/styles";
import Summary from "@app/analytics/Pulse/components/Summary";
import TopicsTable from "@app/analytics/Pulse/components/TopicsTable";
import useForYourTeams from "@app/analytics/Pulse/hooks/useForYourTeams";
import usePulseClips from "@app/analytics/Pulse/hooks/usePulseClips";
import usePulseStore from "@app/analytics/Pulse/stores/pulse";
import type { PulseMetadata } from "@app/analytics/Pulse/types";
import { getAllContributors, getAllSessions } from "@app/analytics/Pulse/utils";
import { pluralizePhrase } from "@app/common/utils";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import Stack, { Row } from "@rui/Stack";

import NotEnoughData from "./NotEnoughData";
import TeamSelector from "./TeamSelector";
import NoAccess from "../components/NoAccess";

const teamIdsForSelectedDateRange = (
  selectedRangeValue: string,
  results: PulseMetadata[]
) => {
  if (!selectedRangeValue) return [];

  const resultsForDateRange = results.filter(
    (result) =>
      result.dateRange.startDate.toFormat("yyyy-MM-dd") === selectedRangeValue
  );

  return compact(map("teamId", resultsForDateRange));
};

const ForYourTeams = () => {
  usePageTitle("For You - Teams");

  const {
    isLoading: isResultsLoading,
    teamResultQuery,
    metadata,
    hasPermissionError,
  } = useForYourTeams();
  const { isInitialLoading: isClipsLoading } = usePulseClips("teams");

  const { availableTeams, selectedRange, selectedTeam } = usePulseStore(
    (state) => state.forYourTeams,
    shallow
  );

  const result = teamResultQuery.data;

  const isInitialLoading = isResultsLoading || isClipsLoading;

  // assume there is data until we know there isn't
  const hasData = isInitialLoading || !isEmpty(result);
  const hasNoResults = !isInitialLoading && isEmpty(metadata);

  const teamIdsForDateRange = teamIdsForSelectedDateRange(
    selectedRange.value,
    metadata
  );
  const teamsForDateRange = availableTeams.filter(({ id }) =>
    teamIdsForDateRange.includes(id)
  );

  const contributors = getAllContributors(result);
  const sessions = getAllSessions(result);

  if (hasPermissionError) {
    return <NoAccess type="team" />;
  }

  return (
    <Content height="100%">
      <Stack spacing={2} flex={1}>
        <Row spacing={1.5} variant="apart">
          <TeamSelector loading={isInitialLoading} teams={teamsForDateRange} />
          <Row variant="right" spacing={2}>
            <SessionListPopover sessions={sessions}>
              <Typography variant="bodySecondaryMed" color="shades.neutral.400">
                {pluralizePhrase(sessions.length, "meeting")} for (
                {selectedRange.label})
              </Typography>
            </SessionListPopover>
            <Contributors contributors={contributors} maxVisible={5} />
          </Row>
        </Row>
        {hasData ? (
          <>
            <Summary
              loading={isInitialLoading}
              content={result?.summary}
              subtitle={`Here's what happened with the ${selectedTeam?.name} team's meetings ${selectedRange.label}`}
              thumbnail={{ src: result?.thumbnailUrl, height: 85 }}
            />
            <TopicsTable
              topics={result?.pulseTopics ?? []}
              loading={isInitialLoading}
            />
          </>
        ) : (
          <NotEnoughData type={hasNoResults ? "all" : "for_team"} />
        )}
      </Stack>
    </Content>
  );
};

export default ForYourTeams;
